import type { LevelConfig } from "@/types/game.types"
import { TILE_SIZE } from "@/data/game-settings"
import { level1Tilemap } from "@/data/tilemaps/level1-tilemap"
import { generateTilemap, levelThemes } from "@/data/tilemaps/generate-tilemap"

/**
 * Convert tile coordinates to world coordinates (center of tile)
 * This ensures all path waypoints are at tile centers for consistent movement
 */
function tileToWorldCenter(tileX: number, tileY: number): { x: number; y: number } {
  return {
    x: tileX * TILE_SIZE + TILE_SIZE / 2,
    y: tileY * TILE_SIZE + TILE_SIZE / 2,
  }
}

/**
 * Convert tile coordinates to world coordinates for 2-tile-wide path center.
 * For a path at tiles (x-1, x), the center is at the boundary between them.
 * This places waypoints at tile edges rather than tile centers.
 *
 * @param tileX - The "anchor" tile X (path is at x-1 and x for vertical, so center is at tileX boundary)
 * @param tileY - The "anchor" tile Y (path is at y-1 and y for horizontal, so center is at tileY boundary)
 */
function tileToPathCenter(tileX: number, tileY: number): { x: number; y: number } {
  // For 2-tile-wide path centered on waypoint, the visual center is at the tile boundary
  // Path tiles are at (tileX-1, tileX) for vertical and (tileY-1, tileY) for horizontal
  // So center is at tileX * TILE_SIZE (left edge of tileX = right edge of tileX-1)
  return {
    x: tileX * TILE_SIZE,
    y: tileY * TILE_SIZE,
  }
}

/**
 * Create a path from an array of tile coordinates
 */
function createPath(tiles: Array<[number, number]>): Array<{ x: number; y: number }> {
  return tiles.map(([tileX, tileY]) => tileToWorldCenter(tileX, tileY))
}

/**
 * Create a path for 2-tile-wide paths where waypoints should be at tile boundaries
 */
function createPath2Wide(tiles: Array<[number, number]>): Array<{ x: number; y: number }> {
  return tiles.map(([tileX, tileY]) => tileToPathCenter(tileX, tileY))
}

const levelDefs: LevelConfig[] = [
  // ===== LEVEL 1: RAT THEME (Nature) =====
  // Shortened path - finish point moved halfway up
  {
    id: "level1",
    name: "The Sewers",
    biome: "grassland",
    tilemap: level1Tilemap,
    // Path ends at halfway point (y=25)
    // Using createPath2Wide for 2-tile-wide path - waypoints at tile boundaries
    path: createPath2Wide([
      [18, -1],   // Entry from top center (path at x=17,18)
      [18, 5],    // Down (path at x=17,18, y=0-5)
      [28, 5],    // Right to far side (path at y=4,5, x=17-28)
      [28, 15],   // Down right side (path at x=27,28, y=4-15)
      [8, 15],    // Across to left (path at y=14,15, x=7-28)
      [8, 25],    // Down left side - END POINT (path at x=7,8, y=14-25)
    ]),
    pathWidth: TILE_SIZE * 2, // 2 tiles wide
    basePosition: tileToPathCenter(8, 25),
    startMoney: 200,
    startLives: 20,
    landingTile: { x: 18, y: 10 },
    waves: [
      // Wave 1 - Normal rats only
      {
        waveNumber: 1,
        enemies: [{ type: "rat", count: 10, spawnInterval: 40 }],
        reward: 50,
      },
      // Wave 2 - Normal + Elite rats
      {
        waveNumber: 2,
        enemies: [
          { type: "rat", count: 8, spawnInterval: 45 },
          { type: "rat:elite", count: 3, spawnInterval: 55 },
        ],
        reward: 70,
      },
      // Wave 3 - Normal + Elite + Unique (Rat King boss)
      {
        waveNumber: 3,
        enemies: [
          { type: "rat", count: 6, spawnInterval: 50 },
          { type: "rat:elite", count: 4, spawnInterval: 55 },
          { type: "rat:unique", count: 1, spawnInterval: 0 },
        ],
        reward: 100,
      },
    ],
  },

  // ===== LEVEL 2: GOBLIN THEME (Humanoid) =====
  {
    id: "level2",
    name: "Goblin Camp",
    path: createPath([
      [-1, 5],
      [3, 5],
      [14, 5],
      [14, 10],
      [3, 10],
      [3, 17],
      [14, 17],
      [14, 24],
      [9, 24],
      [9, 31],
    ]),
    pathWidth: TILE_SIZE,
    basePosition: tileToWorldCenter(9, 28),
    startMoney: 200,
    startLives: 20,
    landingTile: { x: 9, y: 13 },
    waves: [
      // Wave 4 - Normal goblins only
      {
        waveNumber: 4,
        enemies: [{ type: "goblin", count: 12, spawnInterval: 40 }],
        reward: 80,
      },
      // Wave 5 - Normal + Elite goblins
      {
        waveNumber: 5,
        enemies: [
          { type: "goblin", count: 10, spawnInterval: 45 },
          { type: "goblin:elite", count: 4, spawnInterval: 55 },
        ],
        reward: 100,
      },
      // Wave 6 - Normal + Elite + Unique (Goblin Warlord boss)
      {
        waveNumber: 6,
        enemies: [
          { type: "goblin", count: 8, spawnInterval: 50 },
          { type: "goblin:elite", count: 5, spawnInterval: 55 },
          { type: "goblin:unique", count: 1, spawnInterval: 0 },
        ],
        reward: 140,
      },
    ],
  },

  // ===== LEVEL 3: MUSHROOM THEME (Nature) =====
  {
    id: "level3",
    name: "Fungal Forest",
    path: createPath([
      [19, 9],
      [14, 9],
      [14, 3],
      [3, 3],
      [3, 13],
      [14, 13],
      [14, 20],
      [6, 20],
      [6, 26],
      [9, 26],
      [9, 31],
    ]),
    pathWidth: TILE_SIZE,
    basePosition: tileToWorldCenter(9, 28),
    startMoney: 200,
    startLives: 20,
    landingTile: { x: 10, y: 16 },
    waves: [
      // Wave 7 - Normal mushrooms only
      {
        waveNumber: 7,
        enemies: [{ type: "mushroom", count: 10, spawnInterval: 45 }],
        reward: 120,
      },
      // Wave 8 - Normal + Elite mushrooms
      {
        waveNumber: 8,
        enemies: [
          { type: "mushroom", count: 8, spawnInterval: 50 },
          { type: "mushroom:elite", count: 4, spawnInterval: 60 },
        ],
        reward: 150,
      },
      // Wave 9 - Normal + Elite + Unique (Myconic Tyrant boss)
      {
        waveNumber: 9,
        enemies: [
          { type: "mushroom", count: 6, spawnInterval: 55 },
          { type: "mushroom:elite", count: 5, spawnInterval: 60 },
          { type: "mushroom:unique", count: 1, spawnInterval: 0 },
        ],
        reward: 200,
      },
    ],
  },

  // ===== LEVEL 4: LIZARDMAN THEME (Humanoid) =====
  {
    id: "level4",
    name: "Swamp Fortress",
    path: createPath([
      [9, -1],
      [9, 5],
      [3, 5],
      [3, 12],
      [14, 12],
      [14, 19],
      [6, 19],
      [6, 26],
      [9, 26],
      [9, 31],
    ]),
    pathWidth: TILE_SIZE,
    basePosition: tileToWorldCenter(9, 28),
    startMoney: 250,
    startLives: 18,
    landingTile: { x: 10, y: 8 },
    waves: [
      // Wave 10 - Normal lizardmen only
      {
        waveNumber: 10,
        enemies: [{ type: "lizardman", count: 9, spawnInterval: 50 }],
        reward: 180,
      },
      // Wave 11 - Normal + Elite lizardmen
      {
        waveNumber: 11,
        enemies: [
          { type: "lizardman", count: 7, spawnInterval: 55 },
          { type: "lizardman:elite", count: 4, spawnInterval: 65 },
        ],
        reward: 220,
      },
      // Wave 12 - Normal + Elite + Unique (Lizard Chieftain boss)
      {
        waveNumber: 12,
        enemies: [
          { type: "lizardman", count: 5, spawnInterval: 60 },
          { type: "lizardman:elite", count: 5, spawnInterval: 65 },
          { type: "lizardman:unique", count: 1, spawnInterval: 0 },
        ],
        reward: 280,
      },
    ],
  },

  // ===== LEVEL 5: ZOMBIE THEME (Undead) =====
  {
    id: "level5",
    name: "The Graveyard",
    path: createPath([
      [-1, 8],
      [5, 8],
      [5, 3],
      [12, 3],
      [12, 15],
      [5, 15],
      [5, 22],
      [9, 22],
      [9, 31],
    ]),
    pathWidth: TILE_SIZE,
    basePosition: tileToWorldCenter(9, 28),
    startMoney: 300,
    startLives: 16,
    landingTile: { x: 8, y: 10 },
    waves: [
      // Wave 13 - Normal zombies + skeletons
      {
        waveNumber: 13,
        enemies: [
          { type: "zombie", count: 6, spawnInterval: 55 },
          { type: "skeleton", count: 3, spawnInterval: 50 },
        ],
        reward: 260,
      },
      // Wave 14 - Normal + Elite zombies and skeletons
      {
        waveNumber: 14,
        enemies: [
          { type: "zombie", count: 5, spawnInterval: 60 },
          { type: "zombie:elite", count: 3, spawnInterval: 70 },
          { type: "skeleton", count: 3, spawnInterval: 55 },
          { type: "skeleton:elite", count: 2, spawnInterval: 65 },
        ],
        reward: 320,
      },
      // Wave 15 - Normal + Elite + Unique (Zombie Lord boss + skeletons)
      {
        waveNumber: 15,
        enemies: [
          { type: "zombie", count: 4, spawnInterval: 65 },
          { type: "zombie:elite", count: 4, spawnInterval: 70 },
          { type: "zombie:unique", count: 1, spawnInterval: 0 },
          { type: "skeleton", count: 2, spawnInterval: 60 },
          { type: "skeleton:elite", count: 2, spawnInterval: 65 },
        ],
        reward: 400,
      },
    ],
  },

  // ===== LEVEL 6: ORC THEME (Humanoid) =====
  {
    id: "level6",
    name: "Orcish Warcamp",
    path: createPath([
      [9, -1],
      [9, 4],
      [14, 4],
      [14, 11],
      [3, 11],
      [3, 18],
      [14, 18],
      [14, 25],
      [9, 25],
      [9, 31],
    ]),
    pathWidth: TILE_SIZE,
    basePosition: tileToWorldCenter(9, 28),
    startMoney: 350,
    startLives: 15,
    landingTile: { x: 8, y: 14 },
    waves: [
      // Wave 16 - Normal orcs only
      {
        waveNumber: 16,
        enemies: [{ type: "orc", count: 7, spawnInterval: 60 }],
        reward: 380,
      },
      // Wave 17 - Normal + Elite orcs
      {
        waveNumber: 17,
        enemies: [
          { type: "orc", count: 5, spawnInterval: 65 },
          { type: "orc:elite", count: 4, spawnInterval: 75 },
        ],
        reward: 460,
      },
      // Wave 18 - Normal + Elite + Unique (Orc Warchief boss)
      {
        waveNumber: 18,
        enemies: [
          { type: "orc", count: 4, spawnInterval: 70 },
          { type: "orc:elite", count: 5, spawnInterval: 75 },
          { type: "orc:unique", count: 1, spawnInterval: 0 },
        ],
        reward: 560,
      },
    ],
  },

  // ===== LEVEL 7: GHOST THEME (Undead) =====
  {
    id: "level7",
    name: "Haunted Ruins",
    path: createPath([
      [19, 6],
      [12, 6],
      [12, 12],
      [5, 12],
      [5, 6],
      [2, 6],
      [2, 20],
      [9, 20],
      [9, 31],
    ]),
    pathWidth: TILE_SIZE,
    basePosition: tileToWorldCenter(9, 28),
    startMoney: 400,
    startLives: 14,
    landingTile: { x: 7, y: 15 },
    waves: [
      // Wave 19 - Normal ghosts only
      {
        waveNumber: 19,
        enemies: [{ type: "ghost", count: 6, spawnInterval: 65 }],
        reward: 520,
      },
      // Wave 20 - Normal + Elite ghosts
      {
        waveNumber: 20,
        enemies: [
          { type: "ghost", count: 5, spawnInterval: 70 },
          { type: "ghost:elite", count: 4, spawnInterval: 80 },
        ],
        reward: 640,
      },
      // Wave 21 - Normal + Elite + Unique (Phantom Lord boss)
      {
        waveNumber: 21,
        enemies: [
          { type: "ghost", count: 4, spawnInterval: 75 },
          { type: "ghost:elite", count: 5, spawnInterval: 80 },
          { type: "ghost:unique", count: 1, spawnInterval: 0 },
        ],
        reward: 780,
      },
    ],
  },

  // ===== LEVEL 8: GNOLL/WEREWOLF THEME (Supernatural) =====
  {
    id: "level8",
    name: "Werewolf Den",
    path: createPath([
      [9, -1],
      [9, 6],
      [3, 6],
      [3, 13],
      [14, 13],
      [14, 20],
      [9, 20],
      [9, 31],
    ]),
    pathWidth: TILE_SIZE,
    basePosition: tileToWorldCenter(9, 28),
    startMoney: 450,
    startLives: 13,
    landingTile: { x: 8, y: 9 },
    waves: [
      // Wave 22 - Normal werewolves only
      {
        waveNumber: 22,
        enemies: [{ type: "gnoll", count: 5, spawnInterval: 70 }],
        reward: 700,
      },
      // Wave 23 - Normal + Elite werewolves
      {
        waveNumber: 23,
        enemies: [
          { type: "gnoll", count: 4, spawnInterval: 75 },
          { type: "gnoll:elite", count: 3, spawnInterval: 85 },
        ],
        reward: 860,
      },
      // Wave 24 - Normal + Elite + Unique (Werewolf Packmaster boss)
      {
        waveNumber: 24,
        enemies: [
          { type: "gnoll", count: 3, spawnInterval: 80 },
          { type: "gnoll:elite", count: 4, spawnInterval: 85 },
          { type: "gnoll:unique", count: 1, spawnInterval: 0 },
        ],
        reward: 1050,
      },
    ],
  },

  // ===== LEVEL 9: GOLEM THEME (Supernatural) =====
  {
    id: "level9",
    name: "The Foundry",
    path: createPath([
      [-1, 4],
      [6, 4],
      [6, 10],
      [12, 10],
      [12, 4],
      [16, 4],
      [16, 18],
      [9, 18],
      [9, 31],
    ]),
    pathWidth: TILE_SIZE,
    basePosition: tileToWorldCenter(9, 28),
    startMoney: 500,
    startLives: 12,
    landingTile: { x: 9, y: 13 },
    waves: [
      // Wave 25 - Normal golems only
      {
        waveNumber: 25,
        enemies: [{ type: "golem", count: 4, spawnInterval: 80 }],
        reward: 950,
      },
      // Wave 26 - Normal + Elite golems
      {
        waveNumber: 26,
        enemies: [
          { type: "golem", count: 3, spawnInterval: 85 },
          { type: "golem:elite", count: 3, spawnInterval: 95 },
        ],
        reward: 1180,
      },
      // Wave 27 - Normal + Elite + Unique (Golem Colossus boss)
      {
        waveNumber: 27,
        enemies: [
          { type: "golem", count: 3, spawnInterval: 90 },
          { type: "golem:elite", count: 4, spawnInterval: 95 },
          { type: "golem:unique", count: 1, spawnInterval: 0 },
        ],
        reward: 1450,
      },
    ],
  },

  // ===== LEVEL 10: LICH THEME (Undead) =====
  {
    id: "level10",
    name: "Necropolis",
    path: createPath([
      [9, -1],
      [9, 5],
      [14, 5],
      [14, 12],
      [3, 12],
      [3, 19],
      [14, 19],
      [14, 26],
      [9, 26],
      [9, 31],
    ]),
    pathWidth: TILE_SIZE,
    basePosition: tileToWorldCenter(9, 28),
    startMoney: 600,
    startLives: 11,
    landingTile: { x: 8, y: 15 },
    waves: [
      // Wave 28 - Normal liches + skeleton frontline
      {
        waveNumber: 28,
        enemies: [
          { type: "lich", count: 4, spawnInterval: 85 },
          { type: "skeleton", count: 5, spawnInterval: 50 },
          { type: "skeleton:elite", count: 2, spawnInterval: 60 },
        ],
        reward: 1350,
      },
      // Wave 29 - Normal + Elite liches + skeletons
      {
        waveNumber: 29,
        enemies: [
          { type: "lich", count: 3, spawnInterval: 90 },
          { type: "lich:elite", count: 3, spawnInterval: 100 },
          { type: "skeleton", count: 4, spawnInterval: 55 },
          { type: "skeleton:elite", count: 3, spawnInterval: 65 },
        ],
        reward: 1680,
      },
      // Wave 30 - Normal + Elite + Unique (Lich King boss + Death Knight)
      {
        waveNumber: 30,
        enemies: [
          { type: "lich", count: 2, spawnInterval: 95 },
          { type: "lich:elite", count: 4, spawnInterval: 100 },
          { type: "lich:unique", count: 1, spawnInterval: 0 },
          { type: "skeleton", count: 3, spawnInterval: 60 },
          { type: "skeleton:elite", count: 3, spawnInterval: 65 },
          { type: "skeleton:unique", count: 1, spawnInterval: 70 },
        ],
        reward: 2100,
      },
    ],
  },

  // ===== LEVEL 11: VAMPIRE THEME (Supernatural) =====
  {
    id: "level11",
    name: "Castle Crimson",
    path: createPath([
      [19, 8],
      [12, 8],
      [12, 3],
      [5, 3],
      [5, 14],
      [12, 14],
      [12, 22],
      [9, 22],
      [9, 31],
    ]),
    pathWidth: TILE_SIZE,
    basePosition: tileToWorldCenter(9, 28),
    startMoney: 700,
    startLives: 10,
    landingTile: { x: 8, y: 9 },
    waves: [
      // Wave 31 - Normal vampires only
      {
        waveNumber: 31,
        enemies: [{ type: "vampire", count: 3, spawnInterval: 90 }],
        reward: 1900,
      },
      // Wave 32 - Normal + Elite vampires
      {
        waveNumber: 32,
        enemies: [
          { type: "vampire", count: 3, spawnInterval: 95 },
          { type: "vampire:elite", count: 3, spawnInterval: 105 },
        ],
        reward: 2400,
      },
      // Wave 33 - Normal + Elite + Unique (Vampire Lord boss)
      {
        waveNumber: 33,
        enemies: [
          { type: "vampire", count: 2, spawnInterval: 100 },
          { type: "vampire:elite", count: 4, spawnInterval: 105 },
          { type: "vampire:unique", count: 1, spawnInterval: 0 },
        ],
        reward: 3000,
      },
    ],
  },

  // ===== LEVEL 12: ENT THEME (Nature) =====
  {
    id: "level12",
    name: "Ancient Grove",
    path: createPath([
      [9, -1],
      [9, 6],
      [3, 6],
      [3, 14],
      [14, 14],
      [14, 22],
      [9, 22],
      [9, 31],
    ]),
    pathWidth: TILE_SIZE,
    basePosition: tileToWorldCenter(9, 28),
    startMoney: 800,
    startLives: 9,
    landingTile: { x: 8, y: 10 },
    waves: [
      // Wave 34 - Normal ents only
      {
        waveNumber: 34,
        enemies: [{ type: "ent", count: 3, spawnInterval: 95 }],
        reward: 2700,
      },
      // Wave 35 - Normal + Elite ents
      {
        waveNumber: 35,
        enemies: [
          { type: "ent", count: 2, spawnInterval: 100 },
          { type: "ent:elite", count: 3, spawnInterval: 110 },
        ],
        reward: 3400,
      },
      // Wave 36 - Normal + Elite + Unique (Ancient One boss)
      {
        waveNumber: 36,
        enemies: [
          { type: "ent", count: 2, spawnInterval: 105 },
          { type: "ent:elite", count: 4, spawnInterval: 110 },
          { type: "ent:unique", count: 1, spawnInterval: 0 },
        ],
        reward: 4200,
      },
    ],
  },

  // ===== LEVEL 13: BEHOLDER THEME (Supernatural) =====
  {
    id: "level13",
    name: "The Void",
    path: createPath([
      [-1, 6],
      [6, 6],
      [6, 12],
      [12, 12],
      [12, 6],
      [16, 6],
      [16, 20],
      [9, 20],
      [9, 31],
    ]),
    pathWidth: TILE_SIZE,
    basePosition: tileToWorldCenter(9, 28),
    startMoney: 900,
    startLives: 8,
    landingTile: { x: 9, y: 15 },
    waves: [
      // Wave 37 - Normal beholders only
      {
        waveNumber: 37,
        enemies: [{ type: "beholder", count: 3, spawnInterval: 100 }],
        reward: 3800,
      },
      // Wave 38 - Normal + Elite beholders
      {
        waveNumber: 38,
        enemies: [
          { type: "beholder", count: 2, spawnInterval: 105 },
          { type: "beholder:elite", count: 3, spawnInterval: 115 },
        ],
        reward: 4800,
      },
      // Wave 39 - Normal + Elite + Unique (Elder Eye boss)
      {
        waveNumber: 39,
        enemies: [
          { type: "beholder", count: 2, spawnInterval: 110 },
          { type: "beholder:elite", count: 3, spawnInterval: 115 },
          { type: "beholder:unique", count: 1, spawnInterval: 0 },
        ],
        reward: 6000,
      },
    ],
  },

  // ===== LEVEL 14: DEMON THEME (Supernatural) =====
  {
    id: "level14",
    name: "The Final Stand",
    path: createPath([
      [9, -1],
      [9, 5],
      [3, 5],
      [3, 12],
      [14, 12],
      [14, 5],
      [16, 5],
      [16, 20],
      [9, 20],
      [9, 31],
    ]),
    pathWidth: TILE_SIZE,
    basePosition: tileToWorldCenter(9, 28),
    startMoney: 1000,
    startLives: 7,
    landingTile: { x: 8, y: 15 },
    waves: [
      // Wave 40 - Normal demons + imps
      {
        waveNumber: 40,
        enemies: [
          { type: "demon", count: 2, spawnInterval: 110 },
          { type: "imp", count: 4, spawnInterval: 80 },
        ],
        reward: 5500,
      },
      // Wave 41 - Normal + Elite demons and imps
      {
        waveNumber: 41,
        enemies: [
          { type: "demon", count: 2, spawnInterval: 115 },
          { type: "demon:elite", count: 2, spawnInterval: 120 },
          { type: "imp:elite", count: 3, spawnInterval: 90 },
        ],
        reward: 7000,
      },
      // Wave 42 - FINAL WAVE: The Demon Lord and his court
      {
        waveNumber: 42,
        enemies: [
          { type: "demon:elite", count: 3, spawnInterval: 120 },
          { type: "imp:unique", count: 1, spawnInterval: 0 },
          { type: "demon:unique", count: 1, spawnInterval: 0 },
        ],
        reward: 10000,
      },
    ],
  },
]

/**
 * Attach generated tilemaps to every level that doesn't hand-author one.
 * Level 1 keeps its hand-placed map; levels with a theme entry get a
 * deterministic procedural tilemap built from their existing path.
 */
export const levels: LevelConfig[] = levelDefs.map((level) => {
  if (level.tilemap) return level
  const theme = levelThemes[level.id]
  if (!theme) return level
  return {
    ...level,
    tilemap: generateTilemap({
      levelId: level.id,
      path: level.path,
      pathWidth: level.pathWidth,
      landingTile: level.landingTile ?? { x: 9, y: 15 },
      basePosition: level.basePosition,
      theme,
    }),
  }
})

export const getLevelByIndex = (index: number): LevelConfig | undefined => {
  return levels[index]
}

export const getTotalLevels = (): number => {
  return levels.length
}
